/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

import styles from './SupportPictureUploadButton.style';

import messages from 'src/shared/app/support/l10n/supportL10n.json';
import useEventAccentColor from 'src/shared/domain/event/hook/useEventAccentColor';
import CloseButton from 'src/shared/ui/component/button/CloseButton';
import PrimaryButton from 'src/shared/ui/component/button/PrimaryButton';
import InputErrorText from 'src/shared/ui/component/form/InputErrorText';

type Props = {
  field: Object,
  form: Object,
  // Self passed
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const SupportPictureUpload = ({ field, form, intl }: Props): React.Node => {
  const { name } = field;
  const { errors, setFieldValue } = form;
  const error = errors[name];

  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const [accentColor] = useEventAccentColor();

  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [file, setFile] = React.useState(null);

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    setFieldValue(name, file);
  }, [file]);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onClear = () => {
    const { current: inputElt } = inputRef;

    setFile(null);

    if (inputElt) {
      // See comment below
      inputElt.value = null;
    }
  };

  const onClick = () => {
    const { current: inputElt } = inputRef;

    // The input reset must be in the onClear and not the onChange method.
    // Otherwise, it causes a second onChange to fire in IE11 and creates
    // an issue.
    if (inputElt) {
      inputElt.value = null;

      inputElt.click();
    }
  };

  const onInputChange = ({ target }: Event) => {
    if (target instanceof HTMLInputElement) {
      const { files } = target;
      // Files is not an array, but a FileList
      const file = files.length > 0 ? files[0] : null;

      setFile(file);
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles({ accentColor });

  /*
   |----------------------------------------------------------------------------
   | Refs
   |----------------------------------------------------------------------------
   */

  const inputRef = React.createRef();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const closeButton = file && (
    <span className={classes.close}>
      <CloseButton
        layoutClass={classes.clearBtn}
        handleClick={onClear}
        isTrimmed
      />
    </span>
  );

  const errorElt = error && (
    <InputErrorText layoutClass={classes.imageError}>{error}</InputErrorText>
  );

  return (
    <div className={classes.root}>
      <div className={classes.pictureUpload}>
        <PrimaryButton
          layoutClass={classes.screenshotButton}
          handleClick={onClick}
        >
          {intl.formatMessage(messages.userScreenshotFile)}
        </PrimaryButton>
        <span className={classes.fileNameSection}>
          <span className={classes.fileName}>
            {file
              ? file.name
              : intl.formatMessage(messages.userScreenshotFileEmpty)}
          </span>
          {closeButton}
        </span>
      </div>

      <input
        className={classes.screenshotInput}
        onChange={onInputChange}
        // $FlowIssue
        ref={inputRef}
        accept="image/*"
        type="file"
      />
      {errorElt}
    </div>
  );
};

export default compose(React.memo, injectIntl)(SupportPictureUpload);
